import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {BoxedInputIcon} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {BoxedInputStatic} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputStatic';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownEmpty} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {AsyncSearchDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  rowHeightWithDomains,
  rowHeightWithoutDomains,
} from '../../../../common/dropdowns/accountDropdownItem';
import {DropdownEmptyAccountContainer} from '../../../../common/dropdowns/dropdownEmptyAccountContainer';
import {
  InboxesSearchQueryBuilderAsyncDropdownInputConfig,
  InboxesSearchQueryBuilderInputsEnum,
  InboxesSearchQueryBuilderInputValue,
  InputValue,
} from './inboxesSearchQueryBuilderTypes';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderAsyncDropdownInputProps<T>
  extends InboxesSearchQueryBuilderAsyncDropdownInputConfig<T>,
    InboxesSearchQueryBuilderInputValue {}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
  justify-self: start;
  max-width: 100%;
`;

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

/*
 * Component.
 */

export const InboxesSearchQueryBuilderAsyncDropdownInput = <T,>(
  props: InboxesSearchQueryBuilderAsyncDropdownInputProps<T>,
) => {
  const theme = useUserTheme();
  const {id, icon, placeholder, fetcher, makeRowRenderer, inputValue, onChange} = props;
  const {value} = inputValue;
  const [query, setQuery] = useState('');

  const onSelect = useCallback(
    (input: InputValue) =>
      onChange({
        ...inputValue,
        ...input,
      }),
    [inputValue, onChange],
  );

  const rowHeightPredicate = useMemo(
    () => (resource: any) => {
      // For all non-account resources we should use the default height.
      if (id !== InboxesSearchQueryBuilderInputsEnum.ACCOUNT || !resource || !resource.domains) {
        return rowHeightWithoutDomains;
      }

      return resource.domains.length !== 0 ? rowHeightWithDomains : rowHeightWithoutDomains;
    },
    [id],
  );

  const renderDropdownEmpty =
    id === InboxesSearchQueryBuilderInputsEnum.ACCOUNT
      ? renderDropdownEmptyAccount
      : renderDropdownEmptyDefault;

  const renderButton = (isOpen: boolean) => {
    const displayedValue = value.length > 0 ? value : undefined;

    return (
      <BoxedInput size={VisualSizesEnum.MEDIUM} noMargin isButton hasHover={isOpen}>
        {(childProps) => (
          <>
            <BoxedInputIcon {...childProps} iconName={icon} color={theme.greys.shade70} />
            <BoxedInputStatic
              {...childProps}
              noMarginLeft
              value={displayedValue || <StyledPlaceholderSpan>{placeholder}</StyledPlaceholderSpan>}
            />
            <BoxedInputIcon {...childProps} iconName="chevronDownSmall" />
          </>
        )}
      </BoxedInput>
    );
  };

  return (
    <StyledDropdownBoxCoordinator
      renderButton={renderButton}
      renderDropdown={(popoverProps) => (
        <AsyncSearchDropdown
          {...popoverProps}
          headerTitle={props.title}
          query={query}
          onQueryChange={setQuery}
          renderRow={makeRowRenderer(inputValue, onSelect)}
          renderEmpty={renderDropdownEmpty}
          onRequestData={fetcher}
          findRowHeight={rowHeightPredicate}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const renderDropdownEmptyDefault = () => <DropdownEmpty />;

const renderDropdownEmptyAccount = () => <DropdownEmptyAccountContainer />;
